import { useCallback, useEffect, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
	useIdleWarehouses, useUserDetail, useUserPermissions,
	usePlant, useCheckpoints, useOrgs,
} from '../../services/hooks';
import { Button, Checkbox, DatePicker, Dialog, Dropdown, Loader, tCell, toastMessage } from '../utils';
import { buttonClasses } from '../utils/theme';
import { GenericTableInfinite, TablePaginationActions } from '../utils/GenericTable';
import dayjs from 'dayjs';
import { useGetReportFieldsMutation, useGetReportMutation } from '../../api/api';
import { useGetVehiclesQuery } from '../../api/vmsSlice';
import { debounce } from '../../services/functions';
import { Grid, TablePagination } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { downloadExcel } from 'react-export-table-to-excel';
import { AIReportButton } from "./AIReport";
import DateRangePicker from '../utils/GenericDateRangeFilter/GenericDateRangeFilter';
import './Report.css';
import { usePlantContext } from '../../context/plantContext';

// ── Constants ──────────────────────────────────────────────────────────────────
const defaultFilter = {
	startdate: "",
	enddate: "",
	pagesize: 10,
	page_no: 1,
	checkpointType: "gateIn",
};

// All report types with their required permission key.
// To add a new report type: add one entry here — permission check is automatic.
const REPORT_TYPE_OPTIONS = [
	{ label: "Vehicle Movement",       value: "vehicleStats",       permissionKey: "viewReport" },
	{ label: "Fastag Report",          value: "fastagReport",       permissionKey: "fastagReport" },
	{ label: "Vaahan Report For RFID", value: "vaahanReportForRFID", permissionKey: "vaahanReportForRFID" },
	{ label: "Vaahan Report For ANPR", value: "vaahanReportForANPR", permissionKey: "vaahanReportForANPR" },
	{ label: "Sarathi Report",         value: "sarathiReport",      permissionKey: "sarathiReport" },
	{ label: "Device Report",         value: "deviceReport",      permissionKey: "deviceReport" },
];
const DEFAULT_REPORT_TYPE = REPORT_TYPE_OPTIONS[0];
const FIELDS_TO_IGNORE_ALL_SELECT = ["Trip Id"];
const DRAG_TYPE = "FIELD_ITEM";
const checkpointTypes = [
	{ label: "Gate In", value: "gateIn" },
	{ label: "Weighbridge In", value: "weighbridgeIn" },
	{ label: "Godown", value: "godown" },
	{ label: "Weighbridge Out", value: "weighbridgeOut" },
	{ label: "Gate Out", value: "gateOut" },
];

const deviceTypeOptions = [
	{ value: "rfidReader", label: "RFID Reader" },
	{ value: "led", label: "LED" },
	// { value: "speaker", label: "Speaker" },
	{ value: "camera", label: "Camera" },
	{ label: "Workstation", value: "workstation" },
];
// ════════════════════════════════════════════════════════════════════════════════
// EXTRA FILTERS REGISTRY  —  THE ONLY PLACE YOU NEED TO TOUCH
// ════════════════════════════════════════════════════════════════════════════════
//
// To add filters for a new report type, add ONE entry to EXTRA_FILTERS_REGISTRY:
//
//   "myNewReportType": [
//     {
//       renderer:    MyFilterComponent,   // the React component to render
//       filterKeys:  ["myField"],         // state keys to clear on type switch
//       getPayload:  (filter) => ({       // payload fields this filter contributes
//         myParam: filter.myField || "",
//       }),
//     },
//   ],
//
// Then define MyFilterComponent below (receives { config, filter, onFilterChange }).
// That's it — no other changes needed anywhere in the file.
// ════════════════════════════════════════════════════════════════════════════════

const EXTRA_FILTERS_REGISTRY = {
	vehicleStats: [
		{
			renderer:   VehicleSearchFilter,
			showFilterBy: true,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				searchBy: "vehicleNumber",
				search: filter?.vehicleIdName || "",
			}),
		},
		{
			renderer: CheckpointTypeFilter,
			filterKeys: ["checkpointType"],
			getPayload: (filter) => ({
				checkpointType: filter?.checkpointType || "gateIn",
			}),
		}
	],
	fastagReport:[
		{
			renderer:   VehicleSearchFilter,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				vehicleNumber:  filter?.vehicleIdName || "",
			}),
		},
		{
			renderer : ApiResponseFilter,
			filterKeys:["apiResponse"],
			getPayload: (filter) => ({
				searchBy: "statusCategory",
				search: filter?.apiResponse || "",
			}),
		}
	],
	vaahanReportForRFID:[
		{
			renderer:   VehicleSearchFilter,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				vehicleNumber:  filter?.vehicleIdName || "",
			}),
		},
		{
			renderer : ApiResponseFilter,
			filterKeys:["apiResponse"],
			getPayload: (filter) => ({
				searchBy: "statusCategory",
				search: filter?.apiResponse || "",
			}),
		}
	],
	vaahanReportForANPR:[
		{
			renderer:   VehicleSearchFilter,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				vehicleNumber:  filter?.vehicleIdName || "",
			}),
		},
		{
			renderer : ApiResponseFilter,
			filterKeys:["apiResponse"],
			getPayload: (filter) => ({
				searchBy: "statusCategory",
				search: filter?.apiResponse || "",
			}),
		}
	],
	sarathiReport:[
		{
			renderer:   VehicleSearchFilter,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				vehicleNumber:  filter?.vehicleIdName || "",
			}),
		},
		{
			renderer : ApiResponseFilter,
			filterKeys:["apiResponse"],
			getPayload: (filter) => ({
				searchBy: "statusCategory",
				search: filter?.apiResponse || "",
			}),
		}
	],
	deviceReport: [
		{
			renderer: DeviceTypeFilter,
			filterKeys: ["deviceType"],
			getPayload: (filter) => ({
				deviceType: filter?.deviceType || "",
			}),
		}
	]
}
// ── Derived helpers (no need to touch these) ──────────────────────────────────
// Merges all getPayload() results for the active report type into one object.
function getExtraPayloadFields(reportTypeValue, filter) {
	return (EXTRA_FILTERS_REGISTRY[reportTypeValue] || [])
		.reduce((acc, entry) => ({ ...acc, ...entry.getPayload(filter) }), {});
}

// Collects all filterKeys for the active report type into a flat array.
function getExtraFilterKeys(reportTypeValue) {
	return (EXTRA_FILTERS_REGISTRY[reportTypeValue] || [])
		.flatMap(entry => entry.filterKeys || []);
}


// ── Root Component ─────────────────────────────────────────────────────────────
const Report = () => {
	const { userDetail, isSuperAdmin } = useUserDetail();
	const {   userPermissions, isFetched } = useUserPermissions();
	const { plants, getPlantDetails } = usePlant();
	const { getReport, clearData, reportData, isReportLoading, totalCount } = useGetReport();
	const { fetchReportPdf, isPDFLoading } = useGetReportPdf();

	const [idleWarehouseOpen, setIdleWarehouseOpen] = useState(false);
	const [filter, setFilter] = useState(defaultFilter);
	// const [selectedReportType, setSelectedReportType] = useState(DEFAULT_REPORT_TYPE);
	const [selectedFields, setSelectedFields] = useState([]);
	const [customizedFieldEnabled, setCustomizedFieldEnabled] = useState(false);
	const [arrangeDrawerOpen, setArrangeDrawerOpen] = useState(false);


	const { checkpointOptions, getCheckpoints } = useCheckpoints(null, "reportCheckpoints");

	// useEffect(() => {
	// 	if (!isSuperAdmin && plants?.length == 1) {
	// 		getCheckpoints({ forReport: true, plantIds: plants[0]._id });
	// 	}
	// 	clearData();
	// }, []);
    const filteredDefaultReportType =
        isSuperAdmin
            ? REPORT_TYPE_OPTIONS[0]
            : REPORT_TYPE_OPTIONS.find(
                    (option) => userPermissions?.[option.permissionKey]?.value
            );

    const [selectedReportType, setSelectedReportType] = useState(
        filteredDefaultReportType || null
    );
	const { fieldMapping = [], isLoading: fieldMappingLoading } = useReportFieldMapping({ type: selectedReportType?.value });

	useEffect(() => {
		if (fieldMapping?.length && customizedFieldEnabled) {
			setSelectedFields(prev => {
				if (prev.length > 0) return prev;
				return fieldMapping
					.filter(ele => !FIELDS_TO_IGNORE_ALL_SELECT.includes(ele.field_name))
					.map(_ => ({ label: _.field_name, value: _.field_id }));
			});
		}
	}, [fieldMapping]);

	// ── Plant / RO / State derived options ──────────────────────────────────────
	const { plantOpts, stateOpts, roOpts } = plants.reduce(
		(res, plant) => {
			let showPlant = true;
			if (filter.regionalOffice?.length) showPlant = filter.regionalOffice.includes(plant.regionalOffice);
			if (filter.states?.length) showPlant = filter.states.includes(plant.state);
			if (showPlant) res.plantOpts.push({ label: plant.plantName, value: plant._id, data: plant });

			if (plant.regionalOffice && !res.roOpts.find(_ => _.value == plant.regionalOffice)) {
				if (filter.plantIds?.length) {
					if (filter.plantIds.includes(plant._id)) res.roOpts.push({ label: plant.regionalOffice, value: plant.regionalOffice });
				} else if (filter.states?.length) {
					if (filter.states.includes(plant.state)) res.roOpts.push({ label: plant.regionalOffice, value: plant.regionalOffice });
				} else {
					res.roOpts.push({ label: plant.regionalOffice, value: plant.regionalOffice });
				}
			}

			if (plant.state && !res.stateOpts.find(_ => _.value == plant.state)) {
				if (filter.plantIds?.length) {
					if (filter.plantIds.includes(plant._id)) res.stateOpts.push({ label: plant.state, value: plant.state });
				} else if (filter.regionalOffice?.length) {
					if (filter.regionalOffice.includes(plant.regionalOffice)) res.stateOpts.push({ label: plant.state, value: plant.state });
				} else {
					res.stateOpts.push({ label: plant.state, value: plant.state });
				}
			}
			return res;
		},
		{ plantOpts: [], roOpts: [], stateOpts: [] }
	);

let filteredCheckpointTypes = checkpointTypes;
if (filter.plantIds?.length == 1) {
	filteredCheckpointTypes = filteredCheckpointTypes.filter(_ => !!checkpointOptions?.find(c => c.checkpointType == _.value));
}

	// ── Handlers ────────────────────────────────────────────────────────────────
	const handleFilterChange = (e, _name) => {
		const name  = _name ? _name : e.target.name;
		const value = e?.$d
			? new Date(e.$d).toISOString()
			: _name
				? Array.isArray(e) ? e.map(_ => _.value) : e?.value || ""
				: e.target.value;

		// All branches below include page_no: 1 inline when the field is not a
		// pagination key — single atomic setFilter avoids a double-render race.
		const shouldResetPage = name !== "page_no" && name !== "pagesize";
		const pageReset = shouldResetPage ? { page_no: 1 } : {};

		if (name === "vehicleId" || name === "assetsId") {
			setFilter(old => ({ ...old, ...pageReset, [name]: value, [`${name}Name`]: e?.label || "" }));
		} else if (name === "plantId") {
			setFilter(old => ({ ...old, ...pageReset, plantIds: value }));
			getCheckpoints({ forReport: true, plantIds: value });
		} else if (name === "regionalOffice") {
			setFilter(old => ({
				...old,
				...pageReset,
				regionalOffice: value,
				plantIds: value.length && old.plantIds?.length
					? old.plantIds.filter(pid => plants.find(p => p._id == pid && value.includes(p.regionalOffice)))
					: [],
			}));
		} else if (name === "state") {
			setFilter(old => ({
				...old,
				...pageReset,
				states: value,
				plantIds: value.length && old.plantIds?.length
					? old.plantIds.filter(pid => plants.find(p => p._id == pid && value.includes(p.state)))
					: [],
			}));
		} else if (name === "orgId") {
			setFilter(o => ({ ...o, ...pageReset, orgId: value }));
			getPlantDetails({ orgId: value });
		} else {
			setFilter(old => ({ ...old, ...pageReset, [name]: value }));
		}
	};

	const handleReportTypeChange = (option) => {
		setSelectedReportType(option);
		setSelectedFields([]);
		setCustomizedFieldEnabled(false);
		// Auto-clear stale extra-filter state from the previous report type
		const keysToReset = getExtraFilterKeys(selectedReportType.value);
		if (keysToReset.length) {
			setFilter(old => {
				const updated = { ...old };
				keysToReset.forEach(k => { updated[k] = undefined; });
				return updated;
			});
		}
	};

	const handleCustomizedFieldToggle = (e) => {
		const checked = e.target.checked;
		setCustomizedFieldEnabled(checked);
		if (checked) {
			if (!selectedFields.length && fieldMapping?.length) {
				setSelectedFields(
					fieldMapping
						.filter(ele => !FIELDS_TO_IGNORE_ALL_SELECT.includes(ele.field_name))
						.map(_ => ({ label: _.field_name, value: _.field_id }))
				);
			}
			setArrangeDrawerOpen(true);
		} else {
			setSelectedFields([]);
			setArrangeDrawerOpen(false);
		}
	};

	const handleFieldsReorder = (reorderedFields) => setSelectedFields(reorderedFields);

	// Dedicated pagination handlers — avoid routing through the generic handleFilterChange
	// which parses values differently and risks triggering unintended resets.
	const handlePageChange = (newPage) => {
		setFilter(old => ({ ...old, page_no: newPage }));
	};

	const handlePageSizeChange = (newSize) => {
		// Reset to page 1 whenever page size changes
		setFilter(old => ({ ...old, pagesize: newSize, page_no: 1 }));
	};
	const showDateRangeFilter = selectedReportType?.value !== "deviceReport";

	function handleFormValidation(arg = {}) {
		const { type = "html" } = arg;
		let errorMsg = "";
		if (showDateRangeFilter && !filter.startdate) errorMsg = "Please select date range";
		else if (showDateRangeFilter && !filter.enddate) errorMsg = "Please select date range";
		if (errorMsg) { toastMessage(false, errorMsg); return; }

		const payload = makePayload(type);
		if (type === "pdf") handlePdfDownload(payload);
		else getReport(payload);
	}

	function makePayload(download_type = "html") {
		// plantIds: explicit selection wins → derive from RO/State → omit if nothing selected
		let _plantIds = filter.plantIds?.length ? filter.plantIds : undefined;
		if (!_plantIds) {
			let _plants;
			if (filter.regionalOffice?.length) _plants = plants.filter(_ => filter.regionalOffice.includes(_.regionalOffice));
			if (filter.states?.length) _plants = (_plants ?? plants).filter(_ => filter.states.includes(_.state));
			if (_plants?.length) _plantIds = _plants.map(_ => _._id);
		}

		return {
			type: selectedReportType?.value,
			...(showDateRangeFilter && filter.startdate ? { startdate: filter.startdate } : {}),
			...(showDateRangeFilter && filter.enddate   ? { enddate:   filter.enddate   } : {}),
			selected_fields: customizedFieldEnabled
				? selectedFields.map(_ => _.value)
				: (fieldMapping?.length
					? fieldMapping.filter(e => !FIELDS_TO_IGNORE_ALL_SELECT.includes(e.field_name)).map(_ => _.field_id)
					: []),
			orgId: filter?.orgId,
			download_type,
			deviceId: sessionStorage.getItem("deviceId") || "",
			pagesize: filter.pagesize,
			page_no: filter.page_no,
			...(_plantIds ? { plantIds: _plantIds } : {}),
			// Spread in any report-type-specific payload fields (from getExtraPayloadFields)
			...getExtraPayloadFields(selectedReportType?.value, filter),
		};
	}

	const handlePdfDownload = async (payload) => {
		const { data: { error, message, data } } = await fetchReportPdf(payload);
		if (error) {
			toastMessage(false, message);
		} else {
			const byteCharacters = atob(data.toString("base64"));
			const byteArray = new Uint8Array([...byteCharacters].map(c => c.charCodeAt(0)));
			const url = URL.createObjectURL(new Blob([byteArray], { type: "application/pdf" }));
			Object.assign(document.createElement("a"), { href: url, download: `vehicle_report_${new Date()}.pdf` }).click();
			URL.revokeObjectURL(url);
		}
	};

	if (isFetched && userPermissions && Object.keys(userPermissions).length && !userPermissions.viewReport?.value && userDetail?.userType != "superAdmin")
		return <Navigate to="/not-authorized" replace />;

	return (
		<DndProvider backend={HTML5Backend}>
			<div className="contentpanel">
				<div className="cust-row flex-algn-cent" style={{ justifyContent: "space-between" }}>
					<div className="cust-col-5">
						<h1 className="title">Report</h1>
					</div>
					{isSuperAdmin || userPermissions?.viewIdlePlants?.value ? (
						<div style={{ paddingBottom: "5px" }}>
							<Button
								disabled={false}
								text="Idle Warehouses"
								onClick={() => setIdleWarehouseOpen(true)}
								style={buttonClasses.lynkitOrangeFill}
							/>
						</div>
					) : null}
				</div>
				<hr className="bgg" />

				<FilterSection
					selectedReportType={selectedReportType}
					onReportTypeChange={handleReportTypeChange}
					onFilterChange={handleFilterChange}
					filter={filter}
					filteredROptions={roOpts}
                    makePayload={makePayload}
					filteredStatesOptions={stateOpts}
					filteredPlantOptions={plantOpts}
					onSubmit={handleFormValidation}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
					showDateRangeFilter={showDateRangeFilter}
					checkpointTypes={filteredCheckpointTypes}
					customizedFieldEnabled={customizedFieldEnabled}
					onCustomizedFieldToggle={handleCustomizedFieldToggle}
					onOpenDrawer={() => setArrangeDrawerOpen(true)}
				/>

				<ConfigureColumnsDrawer
					open={arrangeDrawerOpen}
					onClose={() => setArrangeDrawerOpen(false)}
					fieldMapping={fieldMapping}
					fieldMappingLoading={fieldMappingLoading}
					selectedFields={selectedFields}
					onReorder={handleFieldsReorder}
				/>

				<IdleWarehouseDialog open={idleWarehouseOpen} onClose={() => setIdleWarehouseOpen(false)} />
			</div>
		</DndProvider>
	);
};


function FilterSection({
	selectedReportType, onReportTypeChange,
	onFilterChange, filteredROptions, filteredStatesOptions, filteredPlantOptions,
	filter, onSubmit, onPageChange, onPageSizeChange, makePayload,
	showDateRangeFilter,
	checkpointTypes,
	customizedFieldEnabled, onCustomizedFieldToggle, onOpenDrawer,
}) {
	const { isSuperAdmin } = useUserDetail();
	const {plantOptions} = usePlantContext();
    const { userPermissions, isFetched } = useUserPermissions();
    const filteredReportTypeOptions = REPORT_TYPE_OPTIONS.filter((option) => {
        if (isSuperAdmin) return true;
        return userPermissions?.[option.permissionKey]?.value;
    });
	const { orgOptions } = useOrgs();
	const orgValue = orgOptions.find(_ => _.value == filter.orgId) || null;

	const regionalOfficeValue = filteredROptions.filter(o => filter.regionalOffice?.includes(o.value));
	const stateValue          = filteredStatesOptions.filter(o => filter.states?.includes(o.value));
	const plantValue          = filteredPlantOptions.filter(o => filter.plantIds?.includes(o.value));

	const { reportData, isReportLoading, totalCount } = useGetReport();
	const { isPDFLoading } = useGetReportPdf();

	// Re-fetch on pagination change
	useEffect(() => {
		if (!reportData) return;
		onSubmit();
	}, [filter.page_no, filter.pagesize]);

	return (
		<>
			<div className="fitler-selection-cont" style={{ marginTop: "15px" }}>
				<Dropdown
					id="reportType"
					name="reportType"
					label="Report Type"
					options={filteredReportTypeOptions}
					value={selectedReportType}
					onChange={onReportTypeChange}
				/>

				{isSuperAdmin ? (
					<Dropdown id="orgId" name="orgId" label="Organization" onChange={onFilterChange} options={orgOptions} value={orgValue} />
				) : null}
				{
					Array.isArray(plantOptions) && plantOptions.length > 1 && (
						<>
							<Dropdown id="regionalOffice" name="regionalOffice" label="Regional Office" onChange={onFilterChange} options={filteredROptions} value={regionalOfficeValue} multiple />
							<Dropdown id="state" name="state" label="State" onChange={onFilterChange} options={filteredStatesOptions} value={stateValue} multiple />
							<Dropdown id="plantFilter" name="plantId" label="Warehouses" onChange={onFilterChange} options={filteredPlantOptions} value={plantValue} multiple disableCloseOnSelect />
						</>
					)
				}

				{/*
				  ── Report-type-specific extra filters ──────────────────────────
				  Driven entirely by EXTRA_FILTERS_REGISTRY.
				  To add filters for a new report type, edit the registry above — not here.
				*/}
				<ExtraFilters
					reportType={selectedReportType?.value}
					filter={filter}
					onFilterChange={onFilterChange}
					checkpointTypes={checkpointTypes}
				/>

				{showDateRangeFilter ? (
					<div>
						<DateRangePicker
							onDateRangeSelect={(data) => {
								// Always propagate — pass empty string when cleared so filter resets properly
								onFilterChange({ value: data?.startDate || "" }, "startdate");
								onFilterChange({ value: data?.endDate   || "" }, "enddate");
							}}
						/>
					</div>
				) : null}
			</div>

			{/* ── Configure Report Columns ── */}
			<div style={{ marginTop: "1rem", display: "flex", alignItems: "center", gap: "1rem" }}>
				<Checkbox
					id="customizedField"
					name="customizedField"
					label="Configure Report Columns"
					checked={customizedFieldEnabled}
					onChange={onCustomizedFieldToggle}
					value="customizedField"
				/>
				{customizedFieldEnabled && (
					<div>
						<Button onClick={onOpenDrawer} text="Edit Columns" style={buttonClasses.lynkitOrangeEmpty} />
					</div>
				)}
			</div>

			{/* ── Action Buttons ── */}
			<Grid container spacing="0.5rem" justifyContent="center" mt="1rem">
				<Grid item xl={2} lg={2} md={2} sm={3} xs={6}>
					<Button
						text="Get Report"
						onClick={() => !isReportLoading && onSubmit()}
						disabled={isReportLoading || isPDFLoading}
						style={buttonClasses.lynkitOrangeFill}
					/>
				</Grid>
				<Grid item xl={2} lg={2} md={2} sm={3} xs={6}>
					<Button
						text="Export To Excel"
						onClick={() => !isReportLoading && onSubmit({ type: "excel" })}
						disabled={isReportLoading || isPDFLoading}
						style={buttonClasses.lynkitGreenFill}
					/>
				</Grid>
                {
                    isSuperAdmin || userPermissions?.viewAIReport?.value ? <Grid item xl={2} lg={2} md={2} sm={3} xs={6}>
                        <AIReportButton makePayload={makePayload} isReportLoading={isReportLoading} isPDFLoading={isPDFLoading} />
                    </Grid> : null
                }
			</Grid>

			{/* ── Report Output ── */}
			{isReportLoading || isPDFLoading ? (
				<Loader />
			) : reportData?.includes("<html>") ? (
				<div>
					<div dangerouslySetInnerHTML={{ __html: reportData?.replace(/\n/g, "") }} className="html-cont" />
					<TablePagination
						component="div"
						count={totalCount}
						page={filter.page_no - 1}
						onPageChange={(eventOrPage, maybePage) => {
							// Custom TablePaginationActions may call (page) instead of MUI standard (event, page)
							const newPage = typeof maybePage === "number" ? maybePage : eventOrPage;
							onPageChange(newPage + 1);
						}}
						rowsPerPage={filter.pagesize}
						onRowsPerPageChange={(e) => onPageSizeChange(Number(e.target.value))}
						ActionsComponent={TablePaginationActions}
						sx={{
							display: "flex", justifyContent: "center", alignItems: "center",
							color: "var(--text-color)", backgroundColor: "var(--table-pagination-bg)",
							"& .MuiTablePagination-selectLabel": { margin: "0" },
							"& .MuiTablePagination-displayedRows": { margin: "0" },
							"& .MuiTablePagination-selectIcon": { color: "var(--text-color)" },
						}}
					/>
				</div>
			) : null}
		</>
	);
}


// ── ExtraFilters ───────────────────────────────────────────────────────────────
// Generic dispatcher — reads EXTRA_FILTERS_REGISTRY and renders each entry.
// Nothing to change here when adding a new report type or a new filter widget.
function ExtraFilters({ reportType, filter, onFilterChange, checkpointTypes }) {
	const entries = EXTRA_FILTERS_REGISTRY[reportType] || [];
	if (!entries.length) return null;

	return (
		<>
			{entries.map((entry, i) => {
				const Renderer = entry.renderer;
				if (!Renderer) return null;
				return (
					<Renderer
						key={`${reportType}-${i}`}
						config={entry}
						filter={filter}
						onFilterChange={onFilterChange}
						checkpointTypes={checkpointTypes}
					/>
				);
			})}
		</>
	);
}
	const ApiResponseOption =[
		{label:"Success", value:"success"},
		{label:"Not Found", value:"not_found"}
	]

// ── VehicleSearchFilter ────────────────────────────────────────────────────────
// Renderer for { type: "vehicleSearch" }.
// Shows "Filter By" (static, always Vehicle Number) + "Search Vehicle Number".
// Reusable: any report type can include { type: "vehicleSearch" } in its config.

function VehicleSearchFilter({ config, filter, onFilterChange }) {
	const filterRef = useRef({ page_no: "1", pagesize: "25", search: "", searchBy: "vehicle" });

	const { data: vehicleData, refetch } = useGetVehiclesQuery(filterRef.current, {
		refetchOnMountOrArgChange: true,
	});
	const { data: vehicles = [] } = vehicleData || {};

	const vehicleValue = filter?.vehicleId
		? vehicles.filter(v => v._id === filter.vehicleId).map(v => ({ label: v.truck_no, value: v._id }))[0] || null
		: null;

	const handleVehicleChange = (e, _name) => {
		const value = _name ? (Array.isArray(e) ? e.map(_ => _.value) : e?.value || "") : e?.target?.value;
		if (!value) {
			filterRef.current = { page_no: "1", pagesize: "25", search: "", searchBy: "vehicle" };
			refetch();
		}
		onFilterChange(e, "vehicleId");
	};

	const handleInputChange = debounce((e) => {
		filterRef.current = { page_no: "1", pagesize: "25", search: e.target.value, searchBy: "vehicle" };
		refetch();
	}, 500);

	return (
		<>
			{config?.showFilterBy ? (
				<Dropdown
					id="filterBy"
					name="filterBy"
					label="Filter By"
					options={[{ label: "Vehicle Number", value: "vehicleNumber" }]}
					value={{ label: "Vehicle Number", value: "vehicleNumber" }}
				/>
			) : null}
			<Dropdown
				id="vehicleId"
				name="vehicleId"
				label="Search Vehicle Number"
				onChange={handleVehicleChange}
				onInputChange={handleInputChange}
				options={vehicles.map(v => ({ label: v.truck_no, value: v._id }))}
				value={vehicleValue}
			/>
		</>
	);
}


function CheckpointTypeFilter({ filter, onFilterChange, checkpointTypes }) {
	return (
		<Dropdown
			id="checkpointType"
			name="checkpointType"
			label="Apply Date Filter For"
			onChange={onFilterChange}
			options={checkpointTypes}
			value={checkpointTypes.find(_ => _.value == filter.checkpointType) || null}
		/>
	);
}
function DeviceTypeFilter({ filter, onFilterChange }) {
	const deviceTypeValue = deviceTypeOptions.find((option) => option.value === filter?.deviceType) || null;

	return (
		<Dropdown
			id="deviceType"
			name="deviceType"
			label="Device Type"
			onChange={onFilterChange}
			options={deviceTypeOptions}
			value={deviceTypeValue}
		/>
	);
}

function ApiResponseFilter({ filter, onFilterChange }) {
	const apiResponseValue =
		ApiResponseOption.find(
			(option) => option.value === filter?.apiResponse
		) || null;

	return (
		<>
			<Dropdown
				id="apiResponseFilterBy"
				name="apiResponseFilterBy"
				label="Filter By"
				options={[{ label: "Status Category", value: "statusCategory" }]}
				value={{ label: "Status Category", value: "statusCategory" }}
			/>
			<Dropdown
				id="apiResponse"
				name="apiResponse"
				label="API Response"
				options={ApiResponseOption}
				value={apiResponseValue}
				onChange={onFilterChange}
			/>
		</>
	);
}
// ── ConfigureColumnsDrawer ─────────────────────────────────────────────────────
function ConfigureColumnsDrawer({ open, onClose, fieldMapping, fieldMappingLoading, selectedFields, onReorder }) {
	const [localFields, setLocalFields]   = useState([]);
	const [checkedValues, setCheckedValues] = useState(new Set());

	useEffect(() => {
		if (!open) return;

		// Always reset first — ensures stale data from a previous report type
		// is never shown while the new fieldMapping is loading or empty.
		if (!fieldMapping?.length) {
			setLocalFields([]);
			return;
		}

		const selected = selectedFields.length
			? [...selectedFields]
			: fieldMapping
				.filter(ele => !FIELDS_TO_IGNORE_ALL_SELECT.includes(ele.field_name))
				.map(_ => ({ label: _.field_name, value: _.field_id }));

		const selectedValues = new Set(selected.map(f => f.value));
		const unselected = fieldMapping
			.filter(f => !selectedValues.has(f.field_id))
			.map(f => ({ label: f.field_name, value: f.field_id }));

		setLocalFields([...selected, ...unselected]);
	}, [open, fieldMapping]);

	useEffect(() => {
		if (!open) return;
		// Reset to empty when fieldMapping is absent, otherwise derive from selectedFields
		setCheckedValues(new Set(
			(selectedFields.length
				? selectedFields
				: fieldMapping?.filter(ele => !FIELDS_TO_IGNORE_ALL_SELECT.includes(ele.field_name)).map(_ => ({ value: _.field_id })) || []
			).map(f => f.value)
		));
	}, [open, fieldMapping]);

	const moveField = useCallback((dragIndex, hoverIndex) => {
		setLocalFields(prev => {
			const next = [...prev];
			const [removed] = next.splice(dragIndex, 1);
			next.splice(hoverIndex, 0, removed);
			return next;
		});
	}, []);

	const handleCheckChange = (field, checked) => {
		setCheckedValues(prev => {
			const next = new Set(prev);
			checked ? next.add(field.value) : next.delete(field.value);
			return next;
		});
	};

	const isAllSelected = fieldMapping?.length
		? checkedValues.size === fieldMapping.length
		: false;

	const handleSelectAll = (e) => {
		setCheckedValues(
			e.target.checked
				? new Set(fieldMapping.map(f => f.field_id))
				: new Set()
		);
	};

	const handleApply = () => {
		onReorder(localFields.filter(f => checkedValues.has(f.value)));
		onClose();
	};

	if (!open) return null;

	return (
		<>
			<div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1200 }} />
			<div className="drawer-panel">
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: "1px solid var(--primary-color, #2e343c)" }}>
					<span style={{ color: "var(--text-color)", fontWeight: 600, fontSize: "1rem" }}>Configure Report Columns</span>
					<button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-color)", fontSize: "1.2rem", padding: "0.2rem 0.4rem" }}>
						✕
					</button>
				</div>

				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 1.25rem 0.4rem", borderBottom: "1px solid var(--primary-color, #2e343c)" }}>
					<Checkbox id="selectAll" name="selectAll" checked={isAllSelected} onChange={handleSelectAll} label="Select All" value="All" />
					<p style={{ margin: 0, fontSize: "0.75rem", color: "var(--text-color)", opacity: 0.5 }}>Drag ⠿ to reorder</p>
				</div>

				<div style={{ flex: 1, overflowY: "auto", padding: "0.25rem 1rem" }}>
					{fieldMappingLoading ? (
						<Loader height="40px" size="1.5rem" />
					) : localFields.length === 0 ? (
						<p style={{ color: "var(--text-color)", opacity: 0.4, fontSize: "0.85rem", textAlign: "center", marginTop: "2rem" }}>No fields available.</p>
					) : (
						localFields.map((field, index) => (
							<DraggableCheckboxItem
								key={field.value}
								index={index}
								field={field}
								checked={checkedValues.has(field.value)}
								moveField={moveField}
								onCheckChange={handleCheckChange}
							/>
						))
					)}
				</div>

				<div style={{ padding: "1rem 1.25rem", borderTop: "1px solid var(--primary-color, #2e343c)", display: "flex", gap: "0.75rem" }}>
					<Button text="Apply" onClick={handleApply} style={buttonClasses.lynkitOrangeFill} />
					<Button text="Cancel" onClick={onClose} style={buttonClasses.lynkitOrangeEmpty} />
				</div>
			</div>
		</>
	);
}


// ── DraggableCheckboxItem ──────────────────────────────────────────────────────
function DraggableCheckboxItem({ field, index, moveField, checked, onCheckChange }) {
	const ref = useRef(null);

	const [{ handlerId }, drop] = useDrop({
		accept: DRAG_TYPE,
		collect: monitor => ({ handlerId: monitor.getHandlerId() }),
		hover(item, monitor) {
			if (!ref.current) return;
			const dragIndex  = item.index;
			const hoverIndex = index;
			if (dragIndex === hoverIndex) return;

			const { top, bottom } = ref.current.getBoundingClientRect();
			const hoverMiddleY = (bottom - top) / 2;
			const hoverClientY = monitor.getClientOffset().y - top;

			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

			moveField(dragIndex, hoverIndex);
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag] = useDrag({
		type: DRAG_TYPE,
		item: () => ({ id: field.value, index }),
		collect: monitor => ({ isDragging: monitor.isDragging() }),
	});

	drag(drop(ref));

	return (
		<div
			ref={ref}
			data-handler-id={handlerId}
			style={{
				display: "flex", alignItems: "center", gap: "0.5rem",
				padding: "0.1rem 0.75rem",
				marginBottom: "0.35rem",
				borderRadius: "8px",
				background: isDragging ? "rgba(245,124,0,0.12)" : "var(--background-color)",
				border: "1px solid var(--primary-color)",
				opacity: isDragging ? 0.45 : 1,
				transition: "background 0.12s ease, opacity 0.12s ease",
				userSelect: "none",
				cursor: "grab",
			}}
		>
			<span style={{ color: "var(--text-color)", opacity: 0.35, fontSize: "1rem", flexShrink: 0, lineHeight: 1 }}>⠿</span>
			<Checkbox
				id={field.value}
				name={field.label}
				label={field.label}
				value={field.value}
				checked={checked}
				onChange={(e) => onCheckChange(field, e.target.checked)}
			/>
		</div>
	);
}


// ── IdleWarehouseDialog 
function IdleWarehouseDialog({ open, onClose }) {
	const [selectedDate, setSelectedDate] = useState(null);
	const [tableData, setTableData]       = useState([]);
	const [hasFetched, setHasFetched]     = useState(false);
	const { getIdleWarehouses, error, isLoading } = useIdleWarehouses();

	const handleSubmit = async () => {
		if (!selectedDate) { toastMessage(false, "Please select a date"); return; }
		try {
			const { data: { error, message, data } } = await getIdleWarehouses({ date: dayjs(selectedDate).format("YYYY-MM-DD") });
			if (error) toastMessage(false, message);
			else { setTableData(data || []); setHasFetched(true); }
		} catch (err) {
			toastMessage(false, err?.data?.message || "Something went wrong");
		}
	};

	const idleWarehouseHeader = ["Warehouse Name"];
	const rows     = tableData.map(row => [tCell(row.plantName || "-")]);
	const excelRows = tableData.map(row => [row.plantName || "-"]);

	const handleDownloadExcel = () => {
		if (!excelRows.length) { toastMessage(false, "No data to download"); return; }
		downloadExcel({ fileName: "Idle_Warehouses", sheet: "Idle Warehouses", tablePayload: { header: idleWarehouseHeader, body: excelRows } });
	};

	const handleClose = () => { setSelectedDate(null); setTableData([]); setHasFetched(false); onClose(); };

	return (
		<Dialog open={open} handleClose={handleClose} title="Idle Warehouses" size="xs" handleSubmit={handleDownloadExcel} SubmitText="Download Excel" isSubmitDisabled={!hasFetched || !excelRows.length}>
			<p style={{ margin: "0 0 1rem 0", color: "var(--text-color)", opacity: 0.7, fontSize: "13px" }}>
				Pick a date to spot warehouses with zero vehicle movement.
			</p>
			<div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
				<DatePicker
					name="idleDate"
					label="Select Date"
					value={selectedDate}
					maxDATE={dayjs()}
					onChange={(val) => { setSelectedDate(val); setHasFetched(false); setTableData([]); }}
				/>
				<div style={{ width: "200px" }}>
					<Button text={isLoading ? "Fetching..." : "Get Data"} onClick={handleSubmit} disabled={isLoading} style={{ ...buttonClasses.lynkitOrangeFill, height: "40px" }} />
				</div>
			</div>
			{isLoading ? (
				<Loader />
			) : !error && hasFetched ? (
				<GenericTableInfinite header={idleWarehouseHeader} rows={rows} isLoading={false} />
			) : null}
		</Dialog>
	);
}

// ── Hooks ──────────────────────────────────────────────────────────────────────
function useReportFieldMapping(payload) {
	const [getFieldMapping, { data, isLoading }] = useGetReportFieldsMutation();
	useEffect(() => {
		if (payload?.type) getFieldMapping({ type: payload.type });
	}, [payload?.type]);
	const { data: fieldMapping } = data || { data: [] };
	return { fieldMapping, isLoading };
}

function useGetReport() {
	const [fetchReport, { data, isLoading: isReportLoading, reset }] = useGetReportMutation({ fixedCacheKey: "getReport" });
	const { data: reportData, totalCount } = data || { data: "" };
	async function getReport(args) {
		const { data: { error, message } } = await fetchReport(args);
		if (error) toastMessage(false, message);
	}
	return { getReport, reportData, isReportLoading, clearData: reset, totalCount };
}

function useGetReportPdf() {
	const [fetchReportPdf, { isLoading: isPDFLoading }] = useGetReportMutation({ fixedCacheKey: "getReportPDF" });
	return { fetchReportPdf, isPDFLoading };
}

export default Report;





















